import { difference, union } from "lodash";

import {
  type Identifier,
  RecordContextProvider,
  RecordRepresentation,
  useListContext,
  useTimeout,
  useTranslate,
} from "ra-core";

import { type MouseEvent, useCallback, useRef } from "react";

import { Link } from "react-router";

import { ReferenceField } from "@/components/admin/reference-field";

import { TextField } from "@/components/admin/text-field";

import { Checkbox } from "@/components/ui/checkbox";

import { Skeleton } from "@/components/ui/skeleton";

import { Button } from "@/components/ui/button";

import { RotateCcw } from "lucide-react";

import type { Contact } from "../types";

import { Avatar } from "./Avatar";

import { TagsList } from "./TagsList";

export const ContactListContent = () => {
  const translate = useTranslate();

  const {
    data: contacts,
    error,
    isPending,
    onToggleItem,
    onSelect,
    selectedIds,
  } = useListContext<Contact>();

  const lastSelected = useRef<Identifier | null>(null);

  const handleToggleItem = useCallback(
    (id: Identifier, event: MouseEvent) => {
      if (!contacts) {
        return;
      }

      const ids = contacts.map((contact) => contact.id);

      const lastSelectedIndex = lastSelected.current
        ? ids.indexOf(lastSelected.current)
        : -1;

      if (event.shiftKey && lastSelectedIndex !== -1) {
        const index = ids.indexOf(id);

        const idsBetweenSelections = ids.slice(
          Math.min(lastSelectedIndex, index),
          Math.max(lastSelectedIndex, index) + 1,
        );

        const isClickedItemSelected = selectedIds?.includes(id);

        const newSelectedIds = isClickedItemSelected
          ? difference(selectedIds, idsBetweenSelections)
          : union(selectedIds, idsBetweenSelections);

        onSelect?.(newSelectedIds);
      } else {
        onToggleItem(id);
      }

      lastSelected.current = id;
    },
    [contacts, selectedIds, onSelect, onToggleItem],
  );

  if (isPending) {
    return <Skeleton className="w-full h-9" />;
  }

  if (error) {
    return null;
  }

  return (
    <div className="rovero-contact-list">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <ContactItemContent
            contact={contact}
            handleToggleItem={handleToggleItem}
          />
        </RecordContextProvider>
      ))}

      {contacts.length === 0 && (
        <div className="p-4">
          <div className="text-muted-foreground">
            {translate("resources.contacts.empty.title", {})}
          </div>
        </div>
      )}
    </div>
  );
};

const ContactItemContent = ({
  contact,
  handleToggleItem,
}: {
  contact: Contact;
  handleToggleItem: (id: Identifier, event: MouseEvent) => void;
}) => {
  const translate = useTranslate();

  const { selectedIds } = useListContext<Contact>();

  const hasCompany = contact.company_id != null;

  const hasSubtitle = !!contact.title || hasCompany;

  return (
    <div className="rovero-contact-capsule flex flex-row items-center pl-2 pr-4 py-2">
      <div
        className="px-4 py-3 flex items-center cursor-pointer"
        onClick={(event) => handleToggleItem(contact.id, event)}
      >
        <Checkbox
          className="cursor-pointer"
          checked={selectedIds.includes(contact.id)}
        />
      </div>

      <Link
        to={`/contacts/${contact.id}/show`}
        className="flex-1 min-w-0 flex flex-row gap-4 items-center"
      >
        <Avatar />

        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">
            {`${contact.first_name} ${contact.last_name ?? ""}`}
          </div>

          {hasSubtitle && (
            <div className="text-sm text-muted-foreground truncate">
              {contact.title && hasCompany
                ? `${translate("resources.contacts.position_at", {
                    title: contact.title,
                  })} `
                : contact.title}

              {hasCompany && (
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link={false}
                >
                  <TextField source="name" />
                </ReferenceField>
              )}
            </div>
          )}
        </div>

        <div className="hidden md:flex max-w-[45%] justify-end">
          <TagsList />
        </div>
      </Link>
    </div>
  );
};

export const ContactListContentMobile = () => {
  const translate = useTranslate();

  const {
    data: contacts,
    error,
    isPending,
    refetch,
  } = useListContext<Contact>();

  const oneSecondHasPassed = useTimeout(1000);

  if (isPending) {
    if (!oneSecondHasPassed) {
      return null;
    }

    return (
      <>
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="flex flex-row items-center py-2 hover:bg-muted transition-colors first:rounded-t-xl last:rounded-b-xl"
          >
            <div className="flex flex-row gap-4 items-center mr-4">
              <Skeleton className="w-10 h-10 rounded-full" />
            </div>

            <div className="flex-1 min-w-0">
              <Skeleton className="w-32 h-5 mb-2" />
              <Skeleton className="w-48 h-4" />
            </div>
          </div>
        ))}
      </>
    );
  }

  if (error && !contacts) {
    return (
      <div className="p-4">
        <div className="text-center text-muted-foreground mb-4">
          {translate("resources.contacts.list.error_loading")}
        </div>

        <div className="text-center mt-2">
          <Button
            onClick={() => {
              refetch();
            }}
          >
            <RotateCcw />

            {translate("crm.common.retry")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <ContactItemContentMobile contact={contact} />
        </RecordContextProvider>
      ))}

      {contacts.length === 0 && (
        <div className="p-4">
          <div className="text-muted-foreground">
            {translate("resources.contacts.empty.title")}
          </div>
        </div>
      )}
    </div>
  );
};

const ContactItemContentMobile = ({ contact }: { contact: Contact }) => {
  const translate = useTranslate();

  const hasCompany = contact.company_id != null;

  const hasSubtitle = !!contact.title || hasCompany;

  return (
    <Link
      to={`/contacts/${contact.id}/show`}
      data-mobile-reveal
      className="rovero-contact-capsule mobile-counterparty-card flex flex-row gap-3 items-center p-4"
    >
      <Avatar />

      <div className="flex-1 min-w-0">
        <div className="font-semibold break-words">
          <RecordRepresentation />
        </div>

        {hasSubtitle && (
          <div className="mt-0.5 text-sm text-muted-foreground">
            {contact.title && hasCompany
              ? `${translate("resources.contacts.position_at", {
                  title: contact.title,
                })} `
              : contact.title}

            {hasCompany && (
              <ReferenceField
                source="company_id"
                reference="companies"
                link={false}
              >
                <TextField source="name" />
              </ReferenceField>
            )}
          </div>
        )}

        <div className="mt-1.5">
          <TagsList />
        </div>
      </div>
    </Link>
  );
};
